/**
 * Bhajan ID Filter Definition
 *
 * Surfaced to users as "Bhajan ID". Internally still keyed `nodeId` for URL
 * compatibility. Implicitly narrows to bhajan-typed nodes (nodeTypeId === 1)
 * so e.g. typing a ragam id never matches in the bhajan browser.
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';

const BHAJAN_NODE_TYPE_ID = 1;

/**
 * Bhajan ID Filter Definition
 */
export const nodeIdFilter: FilterDefinition = {
  id: 'nodeId',
  label: 'Bhajan ID',
  shortLabel: 'Bhajan ID',
  icon: 'HashtagIcon',
  valueType: FilterValueType.Number,
  supportedOperators: [FilterOperator.Equals],
  defaultOperator: FilterOperator.Equals,
  placeholder: 'Enter bhajan ID...',

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const trimmedQuery = query.trim();

    // Only suggest if query looks numeric
    if (!/^\d+$/.test(trimmedQuery)) {
      return [];
    }

    const numericValue = parseInt(trimmedQuery, 10);
    const suggestions: FilterSuggestion[] = [];

    // Only consider bhajans — typing a ragam/talam id should not match here.
    const bhajans = nodes.filter((n) => n.nodeTypeId === BHAJAN_NODE_TYPE_ID);
    const exactMatch = bhajans.find((n) => n.nodeId === numericValue);

    if (exactMatch) {
      // Get display name for the node
      const displayName =
        exactMatch.data?.enName ||
        exactMatch.data?.teName ||
        exactMatch.data?.name ||
        `Bhajan #${numericValue}`;

      suggestions.push({
        filterId: 'nodeId',
        label: `Bhajan ID: ${numericValue}`,
        description: `${displayName}`,
        prefilledOperator: FilterOperator.Equals,
        prefilledValue: numericValue,
        needsConfiguration: false,
        score: 100,
      });
    }
    // Don't suggest exact-id filters when no bhajan with that id exists —
    // the user typed a non-bhajan id and we shouldn't pretend it'll match.
    return suggestions;
  },

  execute(node: GraphDataNode, _operator: FilterOperator, value: FilterValue): boolean {
    // Hidden constraint: the bhajan-id filter never matches non-bhajan nodes,
    // so e.g. ragam #1051 is excluded even if its numeric id matches.
    if (node.nodeTypeId !== BHAJAN_NODE_TYPE_ID) return false;

    const filterValue = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (isNaN(filterValue)) return true;
    return node.nodeId === filterValue;
  },

  getDisplayValue(value: FilterValue): string {
    return String(value);
  },
};
